import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutoSizer, List } from 'react-virtualized';

import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowIcon, CommentIcon, SearchIcon, SendIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import useAutofocusSearchOnOpen from 'hooks/useAutofocusSearchOnOpen';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useFocusSearchOnType from 'hooks/useFocusSearchOnType';

import {
  closeConversationAction,
  fetchTextLeadsAction,
  openConversationAction,
  sendTextMessageAction,
  setTextLeadsSearchAction,
} from 'store/system/actions';
import {
  selectActiveConversationLead,
  selectActiveConversationMessages,
  selectIsConversationLoading,
  selectIsSendingMessage,
  selectTextLeadsList,
  selectTextLeadsLoading,
  selectTextLeadsSearchQuery,
} from 'store/system/selectors';

import {
  containerStyles,
  conversationContainerStyles,
  conversationHeaderNameStyles,
  conversationHeaderStyles,
  emptyStateStyles,
  incomingBubbleStyles,
  inputContainerStyles,
  leadAvatarStyles,
  leadInfoStyles,
  leadItemStyles,
  leadNameStyles,
  leadPreviewStyles,
  leadsListStyles,
  leadTimestampStyles,
  loadingContainerStyles,
  messageAlignLeftStyles,
  messageAlignRightStyles,
  messagesListStyles,
  messageTimestampStyles,
  outgoingBubbleStyles,
  searchContainerStyles,
  searchInputStyles,
  textFieldStyles,
} from './TextMessaging.styles';
import { TextMessagingTestIds } from './TextMessaging.testIds';

const DEBOUNCE_DELAY = 300;

// Lead row height: 12px padding top + 12px padding bottom + 40px avatar = 64px
const LEAD_ROW_HEIGHT = 64;

const getInitials = (firstName, lastName) => {
  const first = (firstName ?? '').charAt(0).toUpperCase();
  const last = (lastName ?? '').charAt(0).toUpperCase();

  return `${first}${last}` || '?';
};

const formatTimestamp = timestamp => {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const LeadItem = memo(({ lead, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(lead);
  }, [lead, onSelect]);

  const initials = useMemo(
    () => getInitials(lead.firstName, lead.lastName),
    [lead.firstName, lead.lastName],
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <Box
      role="button"
      sx={leadItemStyles}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Avatar sx={leadAvatarStyles}>{initials}</Avatar>
      <Box sx={leadInfoStyles}>
        <Typography sx={leadNameStyles} variant="regular14">
          {lead.firstName} {lead.lastName}
        </Typography>
        {lead.lastMessage && (
          <Typography sx={leadPreviewStyles} variant="regular12">
            {lead.lastMessage.text}
          </Typography>
        )}
      </Box>
      {lead.lastMessage?.timestamp && (
        <Typography sx={leadTimestampStyles} variant="regular12">
          {formatTimestamp(lead.lastMessage.timestamp)}
        </Typography>
      )}
    </Box>
  );
});

LeadItem.displayName = 'LeadItem';

LeadItem.propTypes = {
  lead: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lastMessage: PropTypes.shape({
      text: PropTypes.string,
      timestamp: PropTypes.string,
    }),
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

const MessageBubble = memo(({ message }) => {
  const isOutgoing = !message.inBound;
  const bubbleStyles = isOutgoing ? outgoingBubbleStyles : incomingBubbleStyles;
  const alignStyles = isOutgoing ? messageAlignRightStyles : messageAlignLeftStyles;

  return (
    <Box>
      <Box sx={bubbleStyles}>
        <Typography variant="regular14">{message.text}</Typography>
      </Box>
      <Box sx={alignStyles}>
        <Typography sx={messageTimestampStyles} variant="regular12">
          {formatTimestamp(message.timestamp)}
        </Typography>
      </Box>
    </Box>
  );
});

MessageBubble.displayName = 'MessageBubble';

MessageBubble.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
    inBound: PropTypes.bool,
    timestamp: PropTypes.string,
  }).isRequired,
};

const Conversation = memo(() => {
  const dispatch = useDispatch();
  const activeLead = useSelector(selectActiveConversationLead);
  const messages = useSelector(selectActiveConversationMessages);
  const isLoading = useSelector(selectIsConversationLoading);
  const isSending = useSelector(selectIsSendingMessage);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const onBack = useCallback(() => {
    dispatch(closeConversationAction());
  }, [dispatch]);

  const onInputChange = useCallback(e => {
    setInputText(e.target.value);
  }, []);

  const onSend = useCallback(() => {
    if (!inputText.trim() || isSending || !activeLead) {
      return;
    }

    dispatch(
      sendTextMessageAction({
        toPhoneNumber: activeLead.phoneNumber,
        text: inputText.trim(),
      }),
    );
    setInputText('');
  }, [dispatch, inputText, isSending, activeLead]);

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend],
  );

  if (!activeLead) {
    return null;
  }

  return (
    <Box sx={conversationContainerStyles}>
      <Box sx={conversationHeaderStyles}>
        <IconButton data-testid={TextMessagingTestIds.BACK} size="small" onClick={onBack}>
          <ArrowIcon direction={IconDirection.LEFT} size={IconSize[20]} />
        </IconButton>
        <Typography sx={conversationHeaderNameStyles} variant="bold14">
          {activeLead.firstName} {activeLead.lastName}
        </Typography>
        <Typography variant="regular12">{activeLead.phoneNumber}</Typography>
      </Box>

      <Box sx={messagesListStyles}>
        {isLoading && (
          <Box sx={loadingContainerStyles}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!isLoading &&
          messages.map(msg => <MessageBubble key={msg.id ?? msg.timestamp} message={msg} />)}
        {!isLoading && messages.length === 0 && (
          <Box sx={emptyStateStyles}>
            <Typography variant="regular14">No messages yet</Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Box sx={inputContainerStyles}>
        <TextField
          fullWidth
          multiline
          data-testid={TextMessagingTestIds.MESSAGE}
          disabled={isSending}
          maxRows={3}
          placeholder="Type a message..."
          size="small"
          sx={textFieldStyles}
          value={inputText}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
        />
        <IconButton
          color="primary"
          data-testid={TextMessagingTestIds.SEND}
          disabled={!inputText.trim() || isSending}
          size="small"
          onClick={onSend}
        >
          {isSending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
});

Conversation.displayName = 'Conversation';

const searchAdornment = (
  <InputAdornment position="start">
    <SearchIcon size={IconSize[20]} />
  </InputAdornment>
);

const searchInputPropsObj = { startAdornment: searchAdornment };

const TextMessaging = () => {
  const dispatch = useDispatch();
  const leads = useSelector(selectTextLeadsList);
  const isLoading = useSelector(selectTextLeadsLoading);
  const searchQuery = useSelector(selectTextLeadsSearchQuery);
  const activeLead = useSelector(selectActiveConversationLead);
  const searchInputRef = useRef(null);

  useEffectSafeStrictMode(() => {
    dispatch(fetchTextLeadsAction());
  }, [dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    _.debounce(query => {
      dispatch(fetchTextLeadsAction({ phoneNumber: query || undefined }));
    }, DEBOUNCE_DELAY),
    [dispatch],
  );

  const onSearchChange = useCallback(
    e => {
      const query = e.target.value;

      dispatch(setTextLeadsSearchAction(query));
      debouncedFetch(query);
    },
    [dispatch, debouncedFetch],
  );

  const onTypeSearch = useCallback(
    char => {
      dispatch(setTextLeadsSearchAction(char));
      debouncedFetch(char);
    },
    [dispatch, debouncedFetch],
  );

  useEffect(
    () => () => {
      debouncedFetch.cancel();
    },
    [debouncedFetch],
  );

  const onSelectLead = useCallback(
    lead => {
      dispatch(openConversationAction({ lead }));
    },
    [dispatch],
  );

  const renderLeadRow = useCallback(
    ({ index, key, style }) => {
      const lead = leads[index];

      return (
        <div key={key} style={style}>
          <LeadItem lead={lead} onSelect={onSelectLead} />
        </div>
      );
    },
    [leads, onSelectLead],
  );

  const noLeadsRenderer = useCallback(
    () => (
      <Box sx={emptyStateStyles}>
        <CommentIcon size={IconSize[48]} />
        <Typography variant="regular14">No text conversations</Typography>
      </Box>
    ),
    [],
  );

  useAutofocusSearchOnOpen({ isOpen: !activeLead, inputRef: searchInputRef });
  useFocusSearchOnType({ enabled: !activeLead, inputRef: searchInputRef, onType: onTypeSearch });

  if (activeLead) {
    return <Conversation />;
  }

  return (
    <Box sx={containerStyles}>
      <Box sx={searchContainerStyles}>
        <TextField
          fullWidth
          InputProps={searchInputPropsObj}
          data-testid={TextMessagingTestIds.SEARCH}
          inputRef={searchInputRef}
          placeholder="Search by name or number..."
          size="small"
          sx={searchInputStyles}
          value={searchQuery}
          onChange={onSearchChange}
        />
      </Box>

      <Box sx={leadsListStyles}>
        {isLoading && (
          <Box sx={loadingContainerStyles}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!isLoading && (
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                noRowsRenderer={noLeadsRenderer}
                overscanRowCount={5}
                rowCount={leads.length}
                rowHeight={LEAD_ROW_HEIGHT}
                rowRenderer={renderLeadRow}
                width={width}
              />
            )}
          </AutoSizer>
        )}
      </Box>
    </Box>
  );
};

export default memo(TextMessaging);
